<template lang="pug">
  div.iq-tools-view-map-container
    div.iq-tools-view-map-inner
      vue-flow(ref="vueFlow"
            :nodes="nodes"
            :edges="edges"
            @nodes-initialized="layoutGraph"
            @node-click="handleNodeClick"
            :min-zoom="DIAGRAM_ZOOM_MIN"
            :max-zoom="DIAGRAM_ZOOM_MAX"
            :node-types="nodeTypes")
        template(#edge-custom="customEdgeProps")
          custom-edge(v-bind="customEdgeProps")
        controls(position="top-right"
                @interaction-change="onInteractionChange")
</template>

<script>
import {cloneDeep} from 'lodash';
import { nextTick, markRaw} from 'vue';
import { VueFlow } from '@vue-flow/core';
import { Controls } from '@vue-flow/controls';
import WorkflowNode from './Nodes/WorkflowNode.vue';
import DialogNode from './Nodes/DialogNode.vue';
import ResponseNode from './Nodes/ResponseNode.vue';
import CommandNode from './Nodes/CommandNode.vue';
import CustomEdge from './Edges/CustomEdge.vue';
import { IQ_TOOL_DIAGRAM_EDITABLE_NODE_TYPES } from '@/constants/iqtool';

import {
  breadthFirstSearch,
  findLinearChain,
  useWorkflowLayout,
  DIAGRAM_ZOOM_DEFAULT,
  DIAGRAM_ZOOM_MIN,
  DIAGRAM_ZOOM_MAX,
} from './DiagramHelper';

export default {
  name: 'IqToolsDiagram',
  components: {
    VueFlow,
    Controls,
    CustomEdge,
  },
  emits: ['edit'],
  props: {
    initialNodes: {
      type: Array,
      default: () => [],
    },
    initialEdges: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      nodes: null,
      edges: null,
      isLocked: false,
      // Graph indexes for performance
      outgoingBySource: {},
      incomingByTarget: {},
      nodeIndexById: {},
      edgeIndexById: {},
      nodeTypes: {
        workflow: markRaw(WorkflowNode),
        dialog: markRaw(DialogNode),
        response: markRaw(ResponseNode),
        command: markRaw(CommandNode),
      },
      DIAGRAM_ZOOM_DEFAULT,
      DIAGRAM_ZOOM_MIN,
      DIAGRAM_ZOOM_MAX,
    };
  },
  watch: {
    initialNodes: {
      handler(newVal) {
        const newNodeList = cloneDeep(newVal);
        if (Array.isArray(newNodeList)) {
          newNodeList.forEach(node => {
            if (IQ_TOOL_DIAGRAM_EDITABLE_NODE_TYPES.includes(node.type)) {
              node.data.isEditable = true;
              node.data.onEditNode = (type, id, name) => {
                this.$emit('edit', { type, id, name });
              };
            }
          });
        }
        this.nodes = newNodeList;
      },
      deep: true,
      immediate: true,
    },
    initialEdges: {
      handler(newVal) {
        this.edges = cloneDeep(newVal);
      },
      deep: true,
      immediate: true,
    },
  },
  mounted() {
  },
  methods: {
    onInteractionChange(locked) {
      this.isLocked = locked;
    },

    resetDiagram() {
      this.buildGraphIndexes();
      this.resetEdgesVisibility();
      this.recomputeAllHighlights();
    },

    // BFS wrapper: returns ordered list of visited node ids
    bfs(startNodeId, depth = null) {
      return breadthFirstSearch(this.nodes, this.edges, startNodeId, depth);
    },

    // Linear chain wrapper: follows single-child links
    findChain(startNodeId, depth = null) {
      return findLinearChain(this.nodes, this.edges, startNodeId, depth);
    },

    // Build indexes for quick graph operations
    buildGraphIndexes() {
      this.outgoingBySource = {};
      this.incomingByTarget = {};
      this.nodeIndexById = {};
      this.edgeIndexById = {};

      for (let i = 0; i < this.nodes.length; i += 1) {
        const n = this.nodes[i];
        this.nodeIndexById[n.id] = i;
      }

      for (let i = 0; i < this.edges.length; i += 1) {
        const e = this.edges[i];
        this.edgeIndexById[e.id] = i;
        if (!this.outgoingBySource[e.source]) {
          this.outgoingBySource[e.source] = [];
        }
        this.outgoingBySource[e.source].push(e);

        if (!this.incomingByTarget[e.target]) {
          this.incomingByTarget[e.target] = [];
        }
        this.incomingByTarget[e.target].push(e);
      }
    },

    resetEdgesVisibility() {
      for (const e of this.edges) {
        e.hidden = false;
      }
      this.edges = [...this.edges];
    },

    setEdgeHidden(edgeId, hiddenFlag) {
      const idx = this.edgeIndexById[edgeId];
      if (idx === null) {
        return;
      }
      const edge = this.edges[idx];
      edge.hidden = hiddenFlag;
    },

    findEdgeFromTo(sourceId, targetId) {
      const list = this.outgoingBySource[sourceId] || [];
      for (const e of list) {
        if (e.target === targetId) {
          return e;
        }
      }
      return null;
    },

    // Ensures the full linear chain (single-child path) from startId is visible
    expandLinearChainFrom(startId, changedNodeIds = new Set(), changedSourceIds = new Set()) {
      const visited = new Set([startId]);
      let currentId = startId;
      for (;;) {
        const outs = this.outgoingBySource[currentId] || [];
        if (outs.length !== 1) {
          break;
        }
        const edge = outs[0];
        const nextId = edge.target;
        if (visited.has(nextId)) {
          break;
        }
        visited.add(nextId);

        // Unhide edge and node along the chain
        if (edge.hidden) {
          this.setEdgeHidden(edge.id, false);
        }
        if (this.isNodeHidden(nextId)) {
          this.setNodeHidden(nextId, false);
          changedNodeIds.add(nextId);
        }
        changedSourceIds.add(currentId);
        currentId = nextId;
      }
    },

    // Returns all descendant ids (excluding the start) using BFS
    getDescendantsBFS(startId) {
      const visited = new Set([startId]);
      const queue = [startId];
      const result = [];
      while (queue.length) {
        const current = queue.shift();
        const edges = this.outgoingBySource[current] || [];
        for (const e of edges) {
          const t = e.target;
          if (visited.has(t)) {
            continue;
          }
          visited.add(t);
          result.push(t);
          queue.push(t);
        }
      }
      return result;
    },

    isNodeHidden(nodeId) {
      const idx = this.nodeIndexById[nodeId];
      if (idx === null) {
        return true;
      }
      const n = this.nodes[idx];
      return !!(n.hidden || (n.data && n.data.hidden));
    },

    anyVisibleIncoming(nodeId) {
      const incoming = this.incomingByTarget[nodeId] || [];
      for (const e of incoming) {
        if (!e.hidden) {
          return true;
        }
      }
      return false;
    },

    anyVisibleIncomingFromOutside(nodeId, forbiddenSourcesSet) {
      const incoming = this.incomingByTarget[nodeId] || [];
      for (const e of incoming) {
        if (!e.hidden && !forbiddenSourcesSet.has(e.source)) {
          return true;
        }
      }
      return false;
    },

    setNodeHidden(nodeId, hiddenFlag) {
      const idx = this.nodeIndexById[nodeId];
      if (idx === null) {
        return;
      }
      const node = this.nodes[idx];
      // mutate in place for performance
      node.hidden = hiddenFlag;
      node.data = { ...node.data, hidden: hiddenFlag };
    },

    setSourceHighlights(sourceId, handlesArray) {
      const idx = this.nodeIndexById[sourceId];
      if (idx === null) {
        return;
      }
      const node = this.nodes[idx];
      node.data = { ...node.data, highlightedSourceHandles: handlesArray };
    },

    updateHighlightsForSources(sourceIds) {
      for (const sourceId of sourceIds) {
        const edges = this.outgoingBySource[sourceId] || [];
        const highlighted = [];
        for (const e of edges) {
          // Highlight handle if either the target node is hidden or the edge itself is hidden
          if (e.sourceHandle && (this.isNodeHidden(e.target) || e.hidden)) {
            highlighted.push(e.sourceHandle);
          }
        }
        // ensure unique
        this.setSourceHighlights(sourceId, Array.from(new Set(highlighted)));
      }
    },

    recomputeAllHighlights() {
      const allSources = new Set(Object.keys(this.outgoingBySource));
      this.updateHighlightsForSources(allSources);
      this.nodes = [...this.nodes];
    },

    getMyChildren(rootId) {
      return this.edges
        .filter(e => e.source === rootId)
        .map(e => e.target);
    },

    getAllDescendants(rootId) {
      const visited = new Set();
      const descendants = [];

      const visiteNode = (currentId) => {
        if (visited.has(currentId)) {
          return;
        }
        visited.add(currentId);

        const childEdges = this.edges.filter(e => e.source === currentId);

        for (const edge of childEdges) {
          const childNode = this.nodes.find(n => n.id === edge.target);
          if (childNode) {
            descendants.push(childNode.id);
            visiteNode(childNode.id); // recursively visit children
          }
        }
      };

      visiteNode(rootId);

      return descendants;
    },

    async layoutGraph(instance = []) {

      await nextTick();
      await new Promise(r => requestAnimationFrame(r));

      const { layout } = useWorkflowLayout((id) => instance.find(item => item.id === id));

      this.nodes = layout(this.nodes, this.edges, 'TB');

      await nextTick();
      this.$refs.vueFlow.fitView();

      // Reset zoom to default if it's greater than default
      const viewport = this.$refs.vueFlow.getViewport();
      if (viewport.zoom > DIAGRAM_ZOOM_DEFAULT) {
        this.$refs.vueFlow.viewportHelper.zoomTo(DIAGRAM_ZOOM_DEFAULT);
      }

      // Reset index
      this.resetDiagram();
    },

    handleNodeClick({ node }) {
      if (this.isLocked) {
        return;
      }

      const selectedNode = node;
      const childrenEdges = this.outgoingBySource[selectedNode.id] || [];
      const childIds = childrenEdges.map(e => e.target);

      // Determine action based on edge or node visibility:
      // if any immediate child edge is hidden, or any child node is hidden => expand; else collapse
      const anyChildEdgeHidden = childrenEdges.some(e => !!e.hidden);
      const anyChildNodeHidden = childIds.some(id => this.isNodeHidden(id));
      const shouldExpand = anyChildEdgeHidden || anyChildNodeHidden;

      if (shouldExpand) {
        // Expand: show immediate children only
        const changed = new Set();
        for (const cid of childIds) {
          // ensure child visible
          if (this.isNodeHidden(cid)) {
            this.setNodeHidden(cid, false);
            changed.add(cid);
          }
          // show edge from selected -> child
          const edge = this.findEdgeFromTo(selectedNode.id, cid);
          if (edge) {
            this.setEdgeHidden(edge.id, false);
          }
          // expand full linear chain starting from this child
          let currentId = cid;
          const visitedChain = new Set([selectedNode.id, cid]);
          for (;;) {
            const nextEdges = this.outgoingBySource[currentId] || [];
            if (nextEdges.length !== 1) {
              break;
            }
            const nextId = nextEdges[0].target;
            if (visitedChain.has(nextId)) {
              break;
            }
            visitedChain.add(nextId);
            if (this.isNodeHidden(nextId)) {
              this.setNodeHidden(nextId, false);
              changed.add(nextId);
            }
            // show edge current -> next
            const e2 = this.findEdgeFromTo(currentId, nextId);
            if (e2) {
              this.setEdgeHidden(e2.id, false);
            }
            currentId = nextId;
          }
        }
        // Update highlights for the selected node and any sources pointing to changed nodes
        const sourcesToUpdate = new Set([selectedNode.id]);
        for (const nid of changed) {
          const incoming = this.incomingByTarget[nid] || [];
          for (const e of incoming) {
            sourcesToUpdate.add(e.source);
          }
        }
        this.updateHighlightsForSources(sourcesToUpdate);
      } else {
        // Collapse: hide all edges from the collapsed subtree; hide nodes that have no visible incoming edges left
        const descendants = this.getDescendantsBFS(selectedNode.id);
        const collapsedSources = new Set([selectedNode.id, ...descendants]);

        // Fast path: if all immediate children remain visible via another parent, only hide edges from selected -> child
        const childrenEdges = this.outgoingBySource[selectedNode.id] || [];
        const childIds = childrenEdges.map(e => e.target);
        const allChildrenKeptVisible = childIds.length > 0 && childIds.every(cid => this.anyVisibleIncomingFromOutside(cid, collapsedSources));
        if (allChildrenKeptVisible) {
          for (const cid of childIds) {
            const e = this.findEdgeFromTo(selectedNode.id, cid);
            if (e) {
              this.setEdgeHidden(e.id, true);
            }
          }
          // Only the selected node's handles need an update in this case
          this.updateHighlightsForSources(new Set([selectedNode.id]));
          this.nodes = [...this.nodes];
          this.edges = [...this.edges];
          return;
        }

        // Hide edges from subtree sources, except keep edges from sources that are supported by another visible parent outside the subtree
        for (const sourceId of collapsedSources) {
          const outs = this.outgoingBySource[sourceId] || [];
          // Always hide edges from the clicked node itself
          const shouldKeepEdges = sourceId !== selectedNode.id && this.anyVisibleIncomingFromOutside(sourceId, collapsedSources);
          for (const e of outs) {
            if (shouldKeepEdges) {
              continue;
            }
            this.setEdgeHidden(e.id, true);
          }
        }

        // Hide nodes that no longer have any visible incoming edges
        const changed = new Set();
        const hiddenNow = new Set();
        for (const did of descendants) {
          const shouldHide = !this.anyVisibleIncoming(did);
          if (shouldHide && !this.isNodeHidden(did)) {
            this.setNodeHidden(did, true);
            changed.add(did);
            hiddenNow.add(did);
          }
          if (!shouldHide && this.isNodeHidden(did)) {
            // ensure visible if they still have a visible parent
            this.setNodeHidden(did, false);
            changed.add(did);
          }
        }

        // Hide all incoming edges to nodes that were just hidden
        for (const did of hiddenNow) {
          const incoming = this.incomingByTarget[did] || [];
          for (const e of incoming) {
            this.setEdgeHidden(e.id, true);
          }
        }

        // Ensure linear chains from kept-visible nodes are fully opened
        const keptVisible = descendants.filter(d => this.anyVisibleIncoming(d));
        const chainChangedNodes = new Set();
        const chainSources = new Set();
        for (const kv of keptVisible) {
          this.expandLinearChainFrom(kv, chainChangedNodes, chainSources);
        }

        // Update highlights for selected node and any sources that point to changed nodes
        const sourcesToUpdate = new Set([selectedNode.id, ...chainSources]);
        for (const nid of changed) {
          const incoming = this.incomingByTarget[nid] || [];
          for (const e of incoming) {
            sourcesToUpdate.add(e.source);
          }
        }
        for (const nid of chainChangedNodes) {
          const incoming = this.incomingByTarget[nid] || [];
          for (const e of incoming) {
            sourcesToUpdate.add(e.source);
          }
        }
        this.updateHighlightsForSources(sourcesToUpdate);
      }

      // trigger update to propagate changes to VueFlow
      this.nodes = [...this.nodes];
      this.edges = [...this.edges];
    },
  },
};
</script>

<style lang="scss">
@use './Diagram.scss' as *;
</style>

<style scoped>
.iq-tools-view-map-container {
  width: 100%;
  height: 100%;
}

.iq-tools-view-map-inner {
  background-color: #efefef;
  height: 100%;
  width: 100%;
}

/* Custom styling for bigger controls */
:deep(.vue-flow__controls) {
  transform: scale(1.2);
  transform-origin: top right;
}

:deep(.vue-flow__controls-button) {
  width: 20px !important;
  height: 20px !important;
  font-size: 18px !important;
  border-radius: 6px !important;
  margin: 4px !important;
}

:deep(.vue-flow__controls-button svg) {
  width: 20px !important;
  height: 20px !important;
}

:deep(.vue-flow__controls) {
  background: #777;
}
</style>